/**
 * Records and validates merchant payment terms acceptance before linking Stripe/PayPal.
 */

import { AuthManager, BusinessOwner } from '../utils/authManager';
import {
  MERCHANT_PAYMENT_TERMS_VERSION,
  MerchantTermsCheckboxId,
  REQUIRED_MERCHANT_TERMS_IDS,
} from '../constants/merchantPaymentTerms';

export interface MerchantPaymentTermsAcceptance {
  version: string;
  acceptedAt: string;
  acceptedItemIds: MerchantTermsCheckboxId[];
  /** stripe | paypal | both — which connect flow triggered acceptance */
  provider?: 'stripe' | 'paypal' | 'both';
}

export function hasValidMerchantTermsAcceptance(
  owner: BusinessOwner | null | undefined
): boolean {
  const acceptance = owner?.merchantPaymentTermsAcceptance;
  if (!acceptance?.version || !acceptance.acceptedAt) {
    return false;
  }
  if (acceptance.version !== MERCHANT_PAYMENT_TERMS_VERSION) {
    return false;
  }
  const ids = acceptance.acceptedItemIds || [];
  return REQUIRED_MERCHANT_TERMS_IDS.every((id) => ids.includes(id));
}

export function buildMerchantTermsPayload(
  acceptedItemIds: MerchantTermsCheckboxId[],
  provider?: 'stripe' | 'paypal' | 'both'
): MerchantPaymentTermsAcceptance {
  return {
    version: MERCHANT_PAYMENT_TERMS_VERSION,
    acceptedAt: new Date().toISOString(),
    acceptedItemIds: [...acceptedItemIds],
    provider,
  };
}

export async function persistMerchantTermsAcceptance(
  businessId: string,
  acceptedItemIds: MerchantTermsCheckboxId[],
  provider?: 'stripe' | 'paypal' | 'both'
): Promise<MerchantPaymentTermsAcceptance> {
  const acceptance = buildMerchantTermsPayload(acceptedItemIds, provider);

  const owner = await AuthManager.getSavedBusinessOwner();
  if (!owner || owner.id !== businessId) {
    throw new Error('Business owner profile not found. Please sign in again.');
  }

  const prior = owner.merchantPaymentTermsAcceptance;
  let mergedProvider = provider;
  if (
    prior?.provider &&
    provider &&
    prior.provider !== provider &&
    prior.version === MERCHANT_PAYMENT_TERMS_VERSION
  ) {
    mergedProvider = 'both';
  }
  const toSave = mergedProvider
    ? { ...acceptance, provider: mergedProvider }
    : acceptance;

  await AuthManager.saveBusinessOwner({
    ...owner,
    merchantPaymentTermsAcceptance: toSave,
  });

  return toSave;
}

export function getMerchantTermsForApi(
  owner: BusinessOwner | null | undefined
): MerchantPaymentTermsAcceptance | null {
  if (!hasValidMerchantTermsAcceptance(owner)) {
    return null;
  }
  return owner!.merchantPaymentTermsAcceptance!;
}
